import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { nativeCamera } from '@/lib/nativeCamera'
import { IC } from '@/components/camera/recordIcons'

/**
 * The phone's camera wheel (Brian's record.html, Oct 30 2026, and his two
 * rounds from his phone that day).
 *
 * Brian: "make this more user-friendly … a glass overlay … a half fan of
 * options, like a camera wheel you flip with your thumb … the things you
 * highlight turn orange … icons big enough that they make a complete circle,
 * so someone can circle around and see the start again … haptic feedback on
 * the wheel scroll with a light click … when you hit Record it all
 * disappears"; then "a camera's dial at the bottom edge, fewer and bigger
 * buttons"; and "when clicked the circle expands to show the sub-menu
 * selections; after choosing it collapses back down".
 *
 * At rest a glass half-ring hugs the top of Record (behind it, with a gap).
 * Tapped, a glass wheel centred on Record fans out, its lower half off the
 * screen: every setting is a big round button on one continuous ring. Spin it
 * with a thumb (a detent per button, a 20 ms click at a whisper and a haptic
 * tap), tap what you want; the chosen ones are amber; a readout above Record
 * names the button under the notch. A window's corner, the circle's fill, or
 * (under Green screen) Green cloth sits on a smaller arc inside when the circle grows (R 150 → 186). The arrow
 * steps it down the way it grew; a tap on the picture or Record folds it away.
 */

export interface RingItem {
  /** The group, named in the readout (Mode, Layout, You, Camera, Countdown, Speed). */
  g?: string
  k: string
  /** The full name (the readout and the button's label). */
  l: string
  /** The short label under the button. */
  sl?: string
  ic?: ReactNode
  /** A button whose face is its value (the countdown, − and +). */
  tx?: string
  /** A layout icon (drawn taller). */
  lay?: boolean
  dot?: string
  /** The camera-picture fill. */
  cam?: boolean
  on?: boolean
  /** An action, not a choice: never amber. */
  keep?: boolean
  act: () => void
}

// Green screen grows the circle too: its inner arc holds Green cloth (the chroma key), and the circle's fill when you are in one
const HAS_SUB = new Set(['lay:youwin', 'lay:mediawin', 'me:circle', 'mode:green'])
const norm = (a: number) => ((a % 360) + 540) % 360 - 180 // an angle into (-180, 180]

let tickCtx: AudioContext | null = null
function tickSound() {
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AC) return
    if (!tickCtx) tickCtx = new AC()
    if (tickCtx.state === 'suspended') void tickCtx.resume()
    const t = tickCtx.currentTime, o = tickCtx.createOscillator(), g = tickCtx.createGain()
    o.type = 'square'; o.frequency.value = 1900
    g.gain.setValueAtTime(0.02, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.02)
    o.connect(g).connect(tickCtx.destination); o.start(t); o.stop(t + 0.022)
  } catch { /* silent */ }
}

/** The wheel's state, read by checks in development (window.__ggRec.dial). */
export interface DialState { rot: number; step: number; ticks: number; x: boolean; keys: string[] }

export function RecordDial({ open, show, items, sub, wpm, green, onOpen, onClose, frameRef, recRef, stateRef }: {
  /** The wheel is out. */
  open: boolean
  /** The folded ring shows (upright, not recording). */
  show: boolean
  items: RingItem[]
  /** The inner arc's choices for what's chosen (a window's corners, the circle's fills); shown when the circle has grown. */
  sub: RingItem[]
  wpm: number
  green: boolean
  onOpen: () => void
  onClose: () => void
  frameRef: RefObject<HTMLDivElement | null>
  recRef: RefObject<HTMLButtonElement | null>
  stateRef?: { current: DialState | null }
}) {
  const [c, setC] = useState({ x: 196, y: 808 })
  const [rot, setRotState] = useState(0)
  const rotRef = useRef(0)
  const setRot = (r: number) => { rotRef.current = r; setRotState(r) }
  const [x, setX] = useState(false)
  const [anim, setAnim] = useState(false)
  const [last, setLast] = useState<{ k: string; at: number } | null>(null)
  const [, bump] = useState(0)
  const ticks = useRef(0)
  const animT = useRef(0)
  const lastT = useRef(0)
  const dialRef = useRef<HTMLDivElement>(null)

  const R = x ? 186 : 150, RD = R + 40
  const STEP = 360 / Math.max(items.length, 10)
  const S = x ? sub : []

  // centred on Record: measured from the button, in the frame's coordinates
  const place = useCallback(() => {
    const fr = frameRef.current?.getBoundingClientRect(), rb = recRef.current?.getBoundingClientRect()
    if (!fr || !rb || !rb.width) return
    const n = { x: rb.left + rb.width / 2 - fr.left, y: rb.top + rb.height / 2 - fr.top }
    setC((o) => (Math.abs(o.x - n.x) + Math.abs(o.y - n.y) < 0.5 ? o : n))
  }, [frameRef, recRef])
  useLayoutEffect(() => {
    place()
    const raf = requestAnimationFrame(place)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null
    if (ro && frameRef.current) ro.observe(frameRef.current)
    if (ro && recRef.current?.parentElement) ro.observe(recRef.current.parentElement)
    addEventListener('resize', place)
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); removeEventListener('resize', place) }
  }, [place, open, show, frameRef, recRef])

  // folded away: back to the small circle
  useEffect(() => { if (!open) { setX(false); setLast(null) } }, [open])
  useEffect(() => () => { clearTimeout(animT.current); clearTimeout(lastT.current) }, [])

  const tick = () => {
    ticks.current++
    tickSound()
    nativeCamera.haptic()
  }
  const expand = (on: boolean) => {
    setX((was) => {
      if (was === on) return was
      setAnim(true); clearTimeout(animT.current); animT.current = window.setTimeout(() => setAnim(false), 360)
      return on
    })
  }

  const top = items.length ? ((Math.round(-rot / STEP) % items.length) + items.length) % items.length : 0
  if (stateRef) stateRef.current = { rot, step: STEP, ticks: ticks.current, x, keys: items.map((i) => i.k) }

  // spin with a thumb anywhere on the wheel; a still tap on a button is that button; a tap outside the wheel folds it away
  const d0 = useRef<null | { a: number; d: number; rot: number; moved: boolean; k: string | null; sub: boolean }>(null)
  const ang = (e: React.PointerEvent) => {
    const r = dialRef.current!.getBoundingClientRect(), px = e.clientX - r.left - c.x, py = e.clientY - r.top - c.y
    return { a: (Math.atan2(px, -py) * 180) / Math.PI, d: Math.hypot(px, py) }
  }
  const onDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('.close')) return
    const p = ang(e), b = (e.target as HTMLElement).closest<HTMLElement>('.ri')
    d0.current = { a: p.a, d: p.d, rot, moved: false, k: b?.dataset.k ?? null, sub: !!b?.classList.contains('si') }
    try { dialRef.current?.setPointerCapture(e.pointerId) } catch { /* */ }
  }
  const onMove = (e: React.PointerEvent) => {
    const d = d0.current
    if (!d || d.sub) return
    const p = ang(e)
    let da = p.a - d.a
    if (da > 180) da -= 360
    if (da < -180) da += 360
    if (Math.abs(da) > 4 || d.moved) {
      d.moved = true
      setAnim(false)
      const nr = d.rot + da
      if (Math.round(-nr / STEP) !== Math.round(-rotRef.current / STEP)) tick()
      setRot(nr)
    }
  }
  const onUp = () => {
    const d = d0.current
    if (!d) return
    d0.current = null
    if (d.moved) { setRot(Math.round(rotRef.current / STEP) * STEP); return } // settle on the nearest detent
    if (d.k) {
      const it = items.find((i) => i.k === d.k) || sub.find((i) => i.k === d.k)
      if (!it) return
      const wasOn = !!it.on
      it.act()
      setLast({ k: it.k, at: Date.now() })
      clearTimeout(lastT.current); lastT.current = window.setTimeout(() => bump((n) => n + 1), 1900)
      // a choice with more to it grows the circle (again, or back down when it's already chosen); any other shrinks it
      if (d.sub) expand(false)
      else if (HAS_SUB.has(it.k)) expand(!(x && wasOn))
      else expand(false)
      return
    }
    if (d.d > RD + 8) onClose()
  }

  // for checks: a spin that brings a button under the notch, ticking once per detent on the way
  const spinTo = (k: string) => {
    const i = items.findIndex((it) => it.k === k)
    if (i < 0) return false
    const to = -i * STEP, delta = norm(to - rotRef.current), n = Math.round(Math.abs(delta) / STEP)
    for (let j = 0; j < n; j++) tick()
    setRot(rotRef.current + delta)
    return true
  }
  const spinRef = useRef(spinTo); spinRef.current = spinTo
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const w = window as unknown as { __ggRec?: Record<string, unknown> }
    w.__ggRec = { ...(w.__ggRec || {}), spinTo: (k: string) => spinRef.current(k) }
  }, [])

  const btn = (it: RingItem, i: number, inner: boolean, a: number, r: number) => {
    const far = !inner && Math.abs(a) > 104
    const cls = ['ri', it.on && 'on', it.lay && 'lay', it.dot != null && 'dot', inner && 'si', !inner && i === top && 'top', far && 'far'].filter(Boolean).join(' ')
    return (
      <button key={it.k} type="button" className={cls} data-k={it.k} aria-label={it.l} aria-pressed={it.keep ? false : !!it.on}
        tabIndex={far ? -1 : 0} style={{ transform: `rotate(${a}deg) translateY(-${r}px) rotate(${-a}deg)` }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); it.act() } }}>
        {it.tx != null ? <b>{it.tx}</b> : it.dot != null ? (it.cam ? IC.split : <i style={{ background: it.dot }} />) : it.ic}
        {!inner && <small>{it.sl || it.l}</small>}
      </button>
    )
  }

  // the group ticks on the track, all the way round
  const marks: number[] = []
  items.forEach((it, i) => { if (i && it.g !== items[i - 1].g) marks.push(norm((i - 0.5) * STEP + rot)) })
  if (items.length) marks.push(norm(-0.5 * STEP + rot))

  const lastIt = last && Date.now() - last.at < 1800 ? items.find((i) => i.k === last.k) || S.find((i) => i.k === last.k) : null
  const t = lastIt || items[top]
  const vars = { '--cx': `${c.x}px`, '--cy': `${c.y}px`, '--rd': `${RD}px`, '--r': `${R}px` } as CSSProperties

  return (
    <>
      {show && !open && (
        <button className="rec-dtab" type="button" aria-label="Settings: mode, layout, speed, countdown, camera" aria-expanded={false}
          style={vars} onClick={onOpen}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 15l6-6 6 6" /></svg>
        </button>
      )}
      {open && (
        <div ref={dialRef} className={`rec-dial${x ? ' x' : ''}${anim ? ' anim' : ''}`} style={vars} role="group" aria-label="Settings"
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <div className="disc" />
          <div className="ringtrack" />
          <div className="rmarks">{marks.filter((a) => Math.abs(a) <= 104).map((a, i) => <i key={i} style={{ transform: `rotate(${a}deg) translateY(-${R + 32}px)` }} />)}</div>
          <i className="notch" />
          <div className="ring" role="group" aria-label="The wheel">
            {items.map((it, i) => btn(it, i, false, norm(i * STEP + rot), R))}
          </div>
          {S.length > 0 && (
            <div className="subring" role="group" aria-label="More for this choice">
              {(() => { const step = Math.min(24, 168 / Math.max(1, S.length - 1)), a0 = (-step * (S.length - 1)) / 2; return S.map((it, i) => btn(it, i, true, a0 + i * step, 112)) })()}
            </div>
          )}
          <div className="readout" aria-live="polite">
            {t && <><span>{t.g || (green ? 'Circle' : 'Window')}</span>{t.g === 'Speed' ? `${wpm} wpm` : t.l}{!t.keep && t.on && <> <em>on</em></>}</>}
          </div>
          <button className="close" type="button" aria-label="Fold the settings away"
            onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); if (x) expand(false); else onClose() }}>
            <svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
          </button>
        </div>
      )}
    </>
  )
}
